import { GripVertical, Info } from 'lucide-react';
import type { AgeingBandConfig } from '@/types/ageing-analysis';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Column {
  id: string;
  name: string;
  variable: string;
  enabled: boolean;
}

interface FieldsSettings {
  accountSummary: {
    enabled: boolean;
    items: {
      totalOutstanding: boolean;
      totalOverdue: boolean;
      totalNotYetDue: boolean;
      creditLimit: boolean;
      availableCredit: boolean;
      lastPaymentAmount: boolean;
      lastPaymentDate: boolean;
    };
  };
  transactionTable: {
    columns: Column[];
    displayOptions: {
      showBorders: boolean;
      zebraStripe: boolean;
      highlightOverdue: boolean;
      boldOverdue: boolean;
    };
    sortBy: string;
  };
  totals: {
    items: {
      totalInvoiced: boolean;
      totalOutstanding: boolean;
      totalOverdue: boolean;
      totalNotYetDue: boolean;
    };
    alignment: string;
  };
  agingSummary: {
    enabled: boolean;
    buckets: Record<string, boolean>;
    displayStyle: string;
    highlightOverdue: boolean;
  };
}

interface FieldsTabProps {
  settings: FieldsSettings;
  onChange: (settings: FieldsSettings) => void;
  ageingBands?: AgeingBandConfig[];
}

/** Renders a {{token}} as a styled pill */
function TokenPill({ token }: Readonly<{ token: string }>) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-normal bg-muted text-muted-foreground border border-border/60 leading-none whitespace-nowrap">
      {token}
    </span>
  );
}

/** A row with a label (+ optional description + token) on the left and a toggle pinned to the right */
function FieldRow({
  label,
  description,
  variable,
  checked,
  onCheckedChange,
  disabled,
}: Readonly<{
  label: string;
  description?: string;
  variable?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}>) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 min-h-[2rem]">
      <div className="flex flex-col gap-1 min-w-0">
        <Label className="font-normal leading-snug">{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground leading-snug">
            {description}
          </p>
        )}
        {variable && <TokenPill token={variable} />}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0 mt-0.5"
      />
    </div>
  );
}

export function FieldsTab({ settings, onChange, ageingBands }: Readonly<FieldsTabProps>) {
  const updateSettings = (updates: Partial<FieldsSettings>) => {
    onChange({ ...settings, ...updates });
  };

  const enabledColumns = settings.transactionTable.columns.filter(
    (c) => c.enabled,
  ).length;
  const enabledBuckets = Object.values(settings.agingSummary.buckets).filter(
    Boolean,
  ).length;

  return (
    <div className="space-y-4 p-4">
      {/* Panel 1: Account Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="grid grid-cols-[1fr_auto] items-start gap-x-4">
            <div>
              <CardTitle className="text-sm font-semibold">
                Account Summary
              </CardTitle>
              <CardDescription>
                A summary panel showing key account figures. Appears above the
                transaction table.
              </CardDescription>
            </div>
            <Switch
              checked={settings.accountSummary.enabled}
              onCheckedChange={(checked) =>
                updateSettings({
                  accountSummary: {
                    ...settings.accountSummary,
                    enabled: checked,
                  },
                })
              }
              className="shrink-0 mt-0.5"
            />
          </div>
        </CardHeader>
        {settings.accountSummary.enabled && (
          <CardContent>
            <div className="space-y-3 pl-4 border-l-2 border-muted">
              {[
                {
                  key: 'totalOutstanding',
                  label: 'Total Outstanding',
                  variable: '<<totalOutstanding>>',
                },
                {
                  key: 'totalOverdue',
                  label: 'Total Overdue',
                  variable: '<<totalOverdue>>',
                },
                {
                  key: 'totalNotYetDue',
                  label: 'Total Not Yet Due',
                  variable: '<<totalNotYetDue>>',
                },
                {
                  key: 'creditLimit',
                  label: 'Credit Limit',
                  variable: '<<creditLimit>>',
                },
                {
                  key: 'availableCredit',
                  label: 'Available Credit',
                  variable: '<<availableCredit>>',
                },
                {
                  key: 'lastPaymentAmount',
                  label: 'Last Payment Amount',
                  variable: '<<lastPaymentAmount>>',
                },
                {
                  key: 'lastPaymentDate',
                  label: 'Last Payment Date',
                  variable: '<<lastPaymentDate>>',
                },
              ].map((item) => (
                <FieldRow
                  key={item.key}
                  label={item.label}
                  variable={item.variable}
                  checked={
                    settings.accountSummary.items[
                      item.key as keyof typeof settings.accountSummary.items
                    ]
                  }
                  onCheckedChange={(checked) =>
                    updateSettings({
                      accountSummary: {
                        ...settings.accountSummary,
                        items: {
                          ...settings.accountSummary.items,
                          [item.key]: checked,
                        },
                      },
                    })
                  }
                />
              ))}
              <p className="text-xs text-muted-foreground pt-2">
                Figures are calculated at the time the document is generated
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Panel 2: Transaction Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-4">
            <CardTitle className="text-sm font-semibold">
              Transaction Table
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={true} disabled />
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    The transaction table is required for Customer Statements
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Column Selection */}
          <div className="space-y-3">
            <div>
              <Label>Table columns</Label>
              <p className="text-sm text-muted-foreground">
                Choose which columns appear in the transaction table. Drag to
                reorder.
              </p>
            </div>
            <div className="space-y-2">
              {settings.transactionTable.columns.map((column, index) => (
                <div
                  key={column.id}
                  className="grid grid-cols-[auto_1fr_auto] items-start gap-x-2 p-2 rounded border bg-background hover:bg-muted/50 transition-colors"
                >
                  <button
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm leading-snug">{column.name}</span>
                    <TokenPill token={column.variable} />
                  </div>
                  <Switch
                    checked={column.enabled}
                    onCheckedChange={(checked) => {
                      if (!checked && enabledColumns <= 3) return;
                      const newColumns = [...settings.transactionTable.columns];
                      newColumns[index] = { ...column, enabled: checked };
                      updateSettings({
                        transactionTable: {
                          ...settings.transactionTable,
                          columns: newColumns,
                        },
                      });
                    }}
                    className="shrink-0 mt-0.5"
                  />
                </div>
              ))}
            </div>
            {enabledColumns < 3 && (
              <p className="text-sm text-amber-600">
                At least 3 columns are required in the transaction table
              </p>
            )}
          </div>

          {/* Table Display Options */}
          <div className="space-y-3">
            <Label>Table options</Label>
            <div className="space-y-3">
              <FieldRow
                label="Show column borders"
                checked={settings.transactionTable.displayOptions.showBorders}
                onCheckedChange={(checked) =>
                  updateSettings({
                    transactionTable: {
                      ...settings.transactionTable,
                      displayOptions: {
                        ...settings.transactionTable.displayOptions,
                        showBorders: checked,
                      },
                    },
                  })
                }
              />
              <FieldRow
                label="Zebra stripe rows"
                description="Alternating row shading improves readability on long tables"
                checked={settings.transactionTable.displayOptions.zebraStripe}
                onCheckedChange={(checked) =>
                  updateSettings({
                    transactionTable: {
                      ...settings.transactionTable,
                      displayOptions: {
                        ...settings.transactionTable.displayOptions,
                        zebraStripe: checked,
                      },
                    },
                  })
                }
              />
              <FieldRow
                label="Highlight overdue rows"
                description="Overdue rows shown with subtle amber background"
                checked={
                  settings.transactionTable.displayOptions.highlightOverdue
                }
                onCheckedChange={(checked) =>
                  updateSettings({
                    transactionTable: {
                      ...settings.transactionTable,
                      displayOptions: {
                        ...settings.transactionTable.displayOptions,
                        highlightOverdue: checked,
                      },
                    },
                  })
                }
              />
              <FieldRow
                label="Bold overdue amounts"
                checked={settings.transactionTable.displayOptions.boldOverdue}
                onCheckedChange={(checked) =>
                  updateSettings({
                    transactionTable: {
                      ...settings.transactionTable,
                      displayOptions: {
                        ...settings.transactionTable.displayOptions,
                        boldOverdue: checked,
                      },
                    },
                  })
                }
              />
            </div>
          </div>

          {/* Sort */}
          <div className="space-y-2">
            <Label>Sort transactions by</Label>
            <Select
              value={settings.transactionTable.sortBy}
              onValueChange={(value) =>
                updateSettings({
                  transactionTable: {
                    ...settings.transactionTable,
                    sortBy: value,
                  },
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="oldest-first">Oldest first</SelectItem>
                <SelectItem value="newest-first">Newest first</SelectItem>
                <SelectItem value="due-date-asc">Due date ascending</SelectItem>
                <SelectItem value="due-date-desc">
                  Due date descending
                </SelectItem>
                <SelectItem value="invoice-number-asc">
                  Reference number ascending
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Panel 3: Totals */}
      <Card>
        <CardHeader className="pb-3">
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-4">
            <CardTitle className="text-sm font-semibold">Totals</CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={true} disabled />
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Totals are required for Customer Statements</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3 pl-4 border-l-2 border-muted">
            {[
              {
                key: 'totalInvoiced',
                label: 'Total Invoiced',
                variable: '<<totalInvoiced>>',
              },
              {
                key: 'totalOutstanding',
                label: 'Total Outstanding',
                variable: '<<totalOutstanding>>',
              },
              {
                key: 'totalOverdue',
                label: 'Total Overdue',
                variable: '<<totalOverdue>>',
              },
              {
                key: 'totalNotYetDue',
                label: 'Total Not Yet Due',
                variable: '<<totalNotYetDue>>',
              },
            ].map((item) => (
              <FieldRow
                key={item.key}
                label={item.label}
                variable={item.variable}
                checked={
                  settings.totals.items[
                    item.key as keyof typeof settings.totals.items
                  ]
                }
                onCheckedChange={(checked) =>
                  updateSettings({
                    totals: {
                      ...settings.totals,
                      items: { ...settings.totals.items, [item.key]: checked },
                    },
                  })
                }
              />
            ))}
          </div>

          <div className="space-y-2">
            <Label>Totals alignment</Label>
            <ToggleGroup
              type="single"
              value={settings.totals.alignment}
              onValueChange={(value) => {
                if (value)
                  updateSettings({
                    totals: { ...settings.totals, alignment: value },
                  });
              }}
              className="justify-start"
            >
              <ToggleGroupItem value="right" className="flex-1">
                Right aligned
              </ToggleGroupItem>
              <ToggleGroupItem value="full" className="flex-1">
                Full width
              </ToggleGroupItem>
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              Right aligned is standard accounting convention
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Panel 4: Ageing Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="grid grid-cols-[1fr_auto] items-start gap-x-4">
            <div>
              <CardTitle className="text-sm font-semibold">
                Ageing Summary
              </CardTitle>
              <CardDescription>
                A breakdown of outstanding balances by age. Commonly requested
                by customers and auditors.
              </CardDescription>
            </div>
            <Switch
              checked={settings.agingSummary.enabled}
              onCheckedChange={(checked) =>
                updateSettings({
                  agingSummary: { ...settings.agingSummary, enabled: checked },
                })
              }
              className="shrink-0 mt-0.5"
            />
          </div>
        </CardHeader>
        {settings.agingSummary.enabled && (
          <CardContent className="space-y-4">
            <div className="space-y-4 pl-4 border-l-2 border-muted">
              {/* Ageing Buckets */}
              <div className="space-y-3">
                <div>
                  <Label>Ageing buckets</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose which time periods to show
                  </p>
                </div>
                {(ageingBands ?? []).map((band) => {
                  const key = String(band.sequence);
                  const label = band.startDays === 0
                    ? `${band.bandLabel} (not yet due)`
                    : `${band.bandLabel} overdue`;
                  const variable = `<<ageing_band_${band.sequence}>>`;
                  return { key, label, variable };
                }).map((item) => (
                  <FieldRow
                    key={item.key}
                    label={item.label}
                    variable={item.variable}
                    checked={
                      settings.agingSummary.buckets[item.key] ?? false
                    }
                    onCheckedChange={(checked) => {
                      if (!checked && enabledBuckets <= 2) return;
                      updateSettings({
                        agingSummary: {
                          ...settings.agingSummary,
                          buckets: {
                            ...settings.agingSummary.buckets,
                            [item.key]: checked,
                          },
                        },
                      });
                    }}
                  />
                ))}
                {enabledBuckets < 2 && (
                  <p className="text-sm text-amber-600">
                    At least 2 ageing buckets are required
                  </p>
                )}
              </div>

              {/* Display Style */}
              <div className="space-y-2">
                <Label>Display style</Label>
                <ToggleGroup
                  type="single"
                  value={settings.agingSummary.displayStyle}
                  onValueChange={(value) => {
                    if (value)
                      updateSettings({
                        agingSummary: {
                          ...settings.agingSummary,
                          displayStyle: value,
                        },
                      });
                  }}
                  className="justify-start"
                >
                  <ToggleGroupItem value="table" className="flex-1">
                    Table
                  </ToggleGroupItem>
                  <ToggleGroupItem value="list" className="flex-1">
                    Summary list
                  </ToggleGroupItem>
                </ToggleGroup>
                <p className="text-xs text-muted-foreground">
                  Table shows ageing buckets as horizontal table row
                </p>
              </div>

              {/* Highlight Overdue */}
              <FieldRow
                label="Highlight overdue buckets"
                description="Overdue ageing buckets are shown with amber shading"
                checked={settings.agingSummary.highlightOverdue}
                onCheckedChange={(checked) =>
                  updateSettings({
                    agingSummary: {
                      ...settings.agingSummary,
                      highlightOverdue: checked,
                    },
                  })
                }
              />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
